import { initializeDB } from '../config/db';
import { SDS_STORE } from '../config/constants';
import axiosInstance from '../axiosInstance';

/**
 * SDS (Safety Data Sheets) — read-only list of documents from the company database.
 * Metadata is synced into IndexedDB; PDFs are streamed online via /api/sds/{id}/pdf.
 */
const sdsService = {
    /**
     * Read all SDS records from IndexedDB (sorted by name).
     * @returns {Promise<Array<{id: number, name: string, description: string|null, category: string|null, sdsNum: string|null}>>}
     */
    async getAll() {
        const db = await initializeDB();
        const items = await db.getAll(SDS_STORE);
        return items.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    },

    /**
     * Get one record by id.
     */
    async getById(id) {
        const db = await initializeDB();
        return db.get(SDS_STORE, Number(id));
    },

    /**
     * Filter SDS records client-side by name / description / category / SDS#.
     */
    async search(term) {
        const items = await this.getAll();
        if (!term || !term.trim()) return items;
        const q = term.trim().toLowerCase();
        return items.filter(item =>
            (item.name && item.name.toLowerCase().includes(q)) ||
            (item.description && item.description.toLowerCase().includes(q)) ||
            (item.category && item.category.toLowerCase().includes(q)) ||
            (item.sdsNum && item.sdsNum.toLowerCase().includes(q))
        );
    },

    /**
     * Get all distinct non-empty categories (for filter UI).
     * @returns {Promise<string[]>}
     */
    async getCategories() {
        const items = await this.getAll();
        const set = new Set();
        for (const item of items) {
            if (item.category && item.category.trim()) set.add(item.category.trim());
        }
        return [...set].sort((a, b) => a.localeCompare(b));
    },

    /**
     * Fetch the PDF binary (online-only) as a Blob.
     * Pass the Blob to <PdfPreviewModal pdfBlob={...} />, which handles object-URL lifecycle.
     * @returns {Promise<Blob>}
     */
    async getPdfBlob(id) {
        const response = await axiosInstance.get(`/sds/${id}/pdf`, { responseType: 'blob' });
        return new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' });
    },

    /**
     * Ask the backend to email this SDS PDF to the current user.
     * Backend enforces a per-user rate limit and logs to FormSendHistory.
     * @returns {Promise<{success: boolean, message: string}>}
     */
    async emailToMe(id) {
        const response = await axiosInstance.post(`/sds/${id}/email-to-me`);
        return response.data;
    },
};

export default sdsService;
